import { TableCell, TableRow } from '@mui/material';
import { produce } from 'immer';
import { fromJS } from 'immutable';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line no-restricted-imports -- pre-existing EnhancedTable usage; migrate off when touched
import EnhancedTable from '@sophocles/components/EnhancedTable';
import { formatNumberWithCommas } from '@sophocles/utils';

function parseServicesToTableData(services) {
  return fromJS(
    produce(services ?? [], draft => {
      draft.forEach(service => {
        service.title = _.get(service, ['service', 'name'], 'No title');
      });

      return draft;
    }),
  )
    .groupBy(item => item.getIn(['service', 'serviceid']))
    .map(serviceList =>
      serviceList.reduce(
        (reduction, v) => (reduction ? reduction.set('qty', reduction.get('qty', 0) + 1) : v.set('qty', 1)),
        false,
      ),
    )
    .toList()
    .toJS();
}

function ServiceTable({ title, services, loading, ...tableProps }) {
  const { t } = useTranslation();
  const [tableData, setTableData] = useState(parseServicesToTableData(services));

  const columns = [
    {
      id: 'service',
      label: t('projectServices.workForm.columnTitle', 'Title'),
      style: { width: '200px' },
      sortable: true,
      cellRenderer: (datum, row, rowIndex) => {
        return datum.name;
      },
    },
    {
      id: 'qty',
      label: t('projectServices.workForm.columnQty', 'Qty'),
      style: { width: '80px' },
    },
    {
      id: 'price_charged_to_customer',
      label: t('projectServices.workForm.columnRevenue', 'Revenue'),
      style: { width: '80px' },
      cellRenderer: value => '$' + value,
    },
    {
      id: 'price_paid_to_crew',
      label: t('projectServices.workForm.columnPaidToCrew', 'Paid to Crew'),
      style: { width: '80px' },
      cellRenderer: value => '$' + value,
    },
    {
      id: 'extra_paid_to_crew',
      label: t('projectServices.workForm.columnExtraPaidToCrew', 'Extra Paid to Crew'),
      style: { width: '80px' },
      cellRenderer: value => '$' + value,
    },
  ];

  const getTotalForField = field =>
    tableData.reduce((reduction, service) => _.get(service, 'qty', 0) * Number(_.get(service, field)) + reduction, 0);

  const totalCharged = getTotalForField('price_charged_to_customer').toFixed(2);
  const totalPaid = getTotalForField('price_paid_to_crew').toFixed(2);
  const totalExtra = getTotalForField('extra_paid_to_crew').toFixed(2);

  useEffect(() => {
    setTableData(parseServicesToTableData(services));
  }, [services]);

  return (
    <EnhancedTable
      {...tableProps}
      title={title}
      columns={columns}
      rows={tableData}
      defaultOrderBy="title"
      loading={loading}
      footerRow={
        <TableRow style={{ fontWeight: 'bold' }}>
          <TableCell>
            <b>{t('projectServices.workForm.totals', 'Totals')}</b>
          </TableCell>
          <TableCell />
          <TableCell>
            <b>${formatNumberWithCommas(totalCharged)}</b>
          </TableCell>
          <TableCell>
            <b>${formatNumberWithCommas(totalPaid)}</b>
          </TableCell>
          <TableCell>
            <b>${formatNumberWithCommas(totalExtra)}</b>
          </TableCell>
        </TableRow>
      }
    />
  );
}

export default ServiceTable;
